import React, { useMemo, forwardRef } from 'react';
import cm from 'classnames';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { selectThumbnailData } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { Clip, setSelectedClips, selectFileClips } from 'components/app/editor/_mediator/editor.slice';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { getClipConfidenceScore, shouldShowPinButton } from 'components/app/_helpers/_app.helper';
import { useHighlightPin } from './use-highlight-pin';

import { ClipUi } from 'components/app/app-components/track-clip/clip-ui';
import { ClipElement } from 'components/app/app-components/track-clip/clip-element';
import { ClipContainer } from 'components/app/app-components/track-clip/clip-container';
import { ClipRatioContainer } from 'components/app/app-components/track-clip/clip-ratio-container';
import { ClipMenuContainer } from 'components/app/app-components/track-clip/clip-menu';
import { ClipLinking } from 'components/app/app-components/track-clip/clip-linking';
import { ClipPoster } from 'components/app/app-components/track-clip/clip-poster';
import styles from './_selector-clips.module.scss';

interface Props {
  clipData: Clip;
  showClipTooltip: boolean;
}

export const SelectorClip = forwardRef<HTMLDivElement, Props>(({ clipData, showClipTooltip }, srActiveClipRef) => {
  const { srSelectClip } = useSelectorCb();
  const { setHoveredClip } = useClipThumbPrevContext();
  const { activeClip, activeMenuClip, selectedClips, configPanel } = useAppSelector((state) => state.editor);
  const { timeBoundaryClip } = useAppSelector((state) => state.timestamp);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);
  const fileConfigs = useAppSelector((state) => state.app__files.configs);
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const dragger = useAppSelector((state) => state.app.dragger);
  const clips = useAppSelector(selectFileClips);
  const dispatch = useAppDispatch();
  const { togglePin } = useHighlightPin(clipData);

  const isActive = useMemo(() => {
    return clipData.clipId === activeClip;
  }, [activeClip]);

  const isAutoDetected = useMemo(() => {
    return clipData.isAutoHighlight ?? (clipData.score != null && clipData.highlightId != null);
  }, [clipData.score, clipData.highlightId, clipData.isAutoHighlight]);

  const confidenceScore = useMemo(() => getClipConfidenceScore(clipData), [clipData]);
  const showPinButton = useMemo(() => shouldShowPinButton(clipData), [clipData]);

  // Use backend-provided isPinned property (set by ClipService.GetClipsForStory)
  const isPinned = clipData.isPinned ?? false;

  const handleClick = (event: React.MouseEvent) => {
    dispatch(
      setRecentAction({
        key: 'USER',
        desc: `srClip: handleSelect`,
        payload: { clipId: clipData.clipId, shift: event.shiftKey },
      })
    );
    if (event.shiftKey) {
      if (activeClip) {
        if (!clips || !selectedFile) return;
        const targetIndex = clips.findIndex((clipId) => clipId === clipData.clipId);
        const activeIndex = clips.findIndex((clipId) => clipId === activeClip);
        if (targetIndex > activeIndex) dispatch(setSelectedClips(clips.slice(activeIndex, targetIndex + 1)));
        else dispatch(setSelectedClips(clips.slice(targetIndex, activeIndex + 1)));
      } else srSelectClip(clipData.clipId);
    } else srSelectClip(clipData.clipId);
    if (activeClip === clipData.clipId) dispatch(setSelectedClips([]));
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    togglePin();
  };

  return (
    <ClipContainer
      onClick={handleClick}
      ref={isActive ? srActiveClipRef : undefined}
      onMouseEnter={() => showClipTooltip && setHoveredClip(clipData.clipId)}
      className={styles.GridClip}
      modifiers={{
        active: isActive,
        editing: isActive && (dragger === EnumDragger.tsSlider || configPanel === 'clip'),
        boundary: timeBoundaryClip === clipData.clipId,
        menuActive: activeMenuClip === clipData.clipId,
        selected: selectedClips.includes(clipData.clipId),
      }}
    >
      <ClipElement className={isAutoDetected && !isPinned ? styles.unpinnedHighlight : undefined}>
        <ClipMenuContainer clipId={clipData.clipId}>
          <ClipRatioContainer className={styles.GridClip__ratioContainer}>
            <ClipUi clipData={clipData} className={styles.Clip__ui} />
            <ClipPoster
              clipData={clipData}
              fileConfig={fileConfigs[clipData.mediaFileId]}
              thumbnail={selectThumbnailData(clipData.clipId, clipThumbnails)}
            />
            {isAutoDetected && (
              <div className={styles.AutoBadge}>
                <span className={styles.AutoBadge__icon}>✨</span>
                <span className={styles.AutoBadge__text}>Auto</span>
                {confidenceScore !== null && <span className={styles.AutoBadge__score}>{confidenceScore}%</span>}
              </div>
            )}
            {showPinButton && (
              <button
                className={cm(styles.PinButton, {
                  [styles['PinButton--pinned']]: isPinned,
                  [styles['PinButton--topRight']]: true,
                })}
                onClick={handlePinClick}
                title={isPinned ? 'Unpin from storyboard' : 'Pin to storyboard'}
                aria-label={isPinned ? 'Unpin from storyboard' : 'Pin to storyboard'}
              >
                {isPinned ? '📌' : '📍'}
              </button>
            )}
          </ClipRatioContainer>
          <ClipLinking clipData={clipData} />
        </ClipMenuContainer>
      </ClipElement>
    </ClipContainer>
  );
});
